import React, { useState } from 'react';
import { Plus, Clock, CheckCircle, AlertCircle, Wrench, Droplets, Lightbulb, Shield, Dog, MessageCircle, FileText, Camera, Paperclip, ChevronRight, XCircle, Loader2 } from 'lucide-react';
import { useDateFormatter, useFileUpload } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import { Ticket, TicketCategory, TicketStatus } from '@/shared/types';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';

interface TicketListProps {
  tickets: Ticket[];
  onCreate: (ticket: Partial<Ticket>) => void;
  onSelect: (ticket: Ticket) => void;
  canCreate?: boolean;
}

export const TicketList: React.FC<TicketListProps> = ({ tickets, onCreate, onSelect, canCreate = true }) => {
  const { t } = useI18n();
  const { formatDate } = useDateFormatter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'ALL'>('ALL');
  const [newTicket, setNewTicket] = useState<Partial<Ticket>>({
    category: TicketCategory.LEAK,
    title: '',
    description: '',
  });

  // File upload hook
  const { upload, isUploading, reset: resetUpload } = useFileUpload('tickets', {
    allowedTypes: ['image/*'],
  });

  const filteredTickets = statusFilter === 'ALL'
    ? tickets
    : tickets.filter(t => t.status === statusFilter);

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.NEW: return 'bg-blue-50 text-blue-700 border-blue-200';
      case TicketStatus.IN_REVIEW: return 'bg-amber-50 text-amber-700 border-amber-200';
      case TicketStatus.IN_PROCESS: return 'bg-purple-50 text-purple-700 border-purple-200';
      case TicketStatus.RESOLVED: return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
  };

  const getCategoryIcon = (cat: TicketCategory) => {
    switch (cat) {
      case TicketCategory.LEAK: return <Droplets size={16} />;
      case TicketCategory.LIGHTING: return <Lightbulb size={16} />;
      case TicketCategory.SECURITY: return <Shield size={16} />;
      case TicketCategory.COMPLAINT: return <AlertCircle size={16} />;
      case TicketCategory.LOST_PET: return <Dog size={16} />;
      default: return <Wrench size={16} />;
    }
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate) return;
    onCreate(newTicket);
    setIsModalOpen(false);
    setNewTicket({ category: TicketCategory.LEAK, title: '', description: '' });
    resetUpload();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const result = await upload(file);
      if (result) {
        setNewTicket({ ...newTicket, attachmentUrl: result.url });
      }
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden animate-in fade-in duration-300">
        <div className="p-6 border-b border-slate-100">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-800 text-lg">{t('tickets.title')}</h3>
            {canCreate && (
              <Button onClick={() => setIsModalOpen(true)} icon={<Plus size={16}/>}>
                {t('tickets.create')}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(['ALL', TicketStatus.NEW, TicketStatus.IN_REVIEW, TicketStatus.IN_PROCESS, TicketStatus.RESOLVED] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {status === 'ALL' ? t('common.all') : t(`tickets.status.${status.toLowerCase()}`)}
                <span className="ml-1.5 opacity-70">
                  ({status === 'ALL' ? tickets.length : tickets.filter(tk => tk.status === status).length})
                </span>
              </button>
            ))}
          </div>
        </div>

        {filteredTickets.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
               <FileText size={32}/>
            </div>
            <p className="text-slate-400">{statusFilter === 'ALL' ? t('tickets.empty') : t('tickets.no_results')}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredTickets.map((ticket) => (
              <div 
                key={ticket.id} 
                onClick={() => onSelect(ticket)}
                className="p-4 sm:p-6 transition-colors cursor-pointer flex items-center justify-between group"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-slate-600 bg-slate-100 shrink-0`}>
                     {getCategoryIcon(ticket.category)}
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-800 mb-1">{ticket.title}</h4>
                    <p className="text-sm text-slate-500 line-clamp-1 mb-2">{ticket.description}</p>
                    <div className="flex flex-wrap items-center gap-2">
                       <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border uppercase tracking-wider ${getStatusColor(ticket.status)}`}>
                         {t(`tickets.status.${ticket.status.toLowerCase()}`)}
                       </span>
                       <span className="text-xs text-slate-400 flex items-center gap-1">
                         <Clock size={12}/> {formatDate(ticket.updatedAt)}
                       </span>
                       {ticket.comments.length > 0 && (
                         <span className="text-xs text-primary-600 flex items-center gap-1 font-medium bg-primary-50 px-2 py-0.5 rounded-full">
                           <MessageCircle size={10}/> {ticket.comments.length}
                         </span>
                       )}
                    </div>
                  </div>
                </div>
                <div className="text-slate-300 group-hover:text-primary-600 transition-colors">
                  <ChevronRight />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {canCreate && isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 text-lg">{t('tickets.modal.title')}</h3>
              <button onClick={() => setIsModalOpen(false)}><XCircle className="text-slate-400 hover:text-slate-600" size={20}/></button>
            </div>
            
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <Input 
                label={t('tickets.modal.subject')}
                value={newTicket.title || ''}
                onChange={e => setNewTicket({...newTicket, title: e.target.value})}
                required
              />
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets.modal.category')}</label>
                <select 
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm"
                  value={newTicket.category}
                  onChange={e => setNewTicket({...newTicket, category: e.target.value as any})}
                >
                  <option value={TicketCategory.LEAK}>{t('tickets.category.leak')}</option>
                  <option value={TicketCategory.LIGHTING}>{t('tickets.category.lighting')}</option>
                  <option value={TicketCategory.SECURITY}>{t('tickets.category.security')}</option>
                  <option value={TicketCategory.COMPLAINT}>{t('tickets.category.complaint')}</option>
                  <option value={TicketCategory.LOST_PET}>{t('tickets.category.lost_pet')}</option>
                  <option value={TicketCategory.OTHER}>{t('tickets.category.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets.modal.desc')}</label>
                <textarea 
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100"
                  rows={4}
                  value={newTicket.description || ''}
                  onChange={e => setNewTicket({...newTicket, description: e.target.value})}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets.modal.photo')}</label>
                <label className={`cursor-pointer flex flex-col items-center justify-center w-full h-32 border-2 border-slate-300 border-dashed rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors ${isUploading ? 'opacity-50 cursor-wait' : ''}`}>
                   {isUploading ? (
                     <div className="flex flex-col items-center justify-center pt-5 pb-6 text-slate-500">
                        <Loader2 size={24} className="mb-2 animate-spin"/>
                        <p className="text-xs">Subiendo...</p>
                     </div>
                   ) : newTicket.attachmentUrl ? (
                     <img src={newTicket.attachmentUrl} alt="Preview" className="h-full object-contain" />
                   ) : (
                     <div className="flex flex-col items-center justify-center pt-5 pb-6 text-slate-500">
                        <Camera size={24} className="mb-2"/>
                        <p className="text-xs">Click para subir foto</p>
                     </div>
                   )}
                   <input type="file" className="hidden" accept="image/*" disabled={isUploading} onChange={handleFileUpload} />
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="secondary" className="flex-1" onClick={() => setIsModalOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="primary" className="flex-1">
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
